import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fiscalPeriodApi } from '../services/accountingApi';
import { FiscalPeriodCreate, GenerateFiscalYearRequest, ListFiscalPeriodsParams } from '../types';

export const FISCAL_PERIODS_QUERY_KEY = 'fiscal-periods';

/**
 * @param options.enabled Skip the request while it can't matter — e.g. the
 *   journal-entry dialog only needs periods while it is open, and mounting a
 *   closed dialog should not cost a request.
 */
export function useFiscalPeriods(
  params?: ListFiscalPeriodsParams,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: [FISCAL_PERIODS_QUERY_KEY, params],
    queryFn: () => fiscalPeriodApi.list(params),
    enabled: options?.enabled ?? true,
    // Filters are part of the key, so without this the list blanks (and `isLoading`
    // goes true again) on every year/status change — see ui-list-pages.md § 2.
    placeholderData: keepPreviousData,
  });
}

export function useFiscalPeriod(id: string) {
  return useQuery({
    queryKey: [FISCAL_PERIODS_QUERY_KEY, id],
    queryFn: () => fiscalPeriodApi.get(id),
    enabled: !!id,
  });
}

export function useCreateFiscalPeriod() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: FiscalPeriodCreate) => fiscalPeriodApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FISCAL_PERIODS_QUERY_KEY] });
    },
  });
}

export function useGenerateFiscalYear() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: GenerateFiscalYearRequest) => fiscalPeriodApi.generateYear(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FISCAL_PERIODS_QUERY_KEY] });
    },
  });
}

export function useLockFiscalPeriod() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => fiscalPeriodApi.lock(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FISCAL_PERIODS_QUERY_KEY] });
    },
  });
}
